<template>
  <div class="rendering-config flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Rendering Config</span>
    </div>

    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="bootLoading">
      <Spinner />
    </div>

    <div v-else class="a-body flex-grow-1 overflow-y-auto p-20-px">
      <a-tabs :activeKey="activeTab" type="card" @change="onTabChange">
        <a-tab-pane :key="tabs.customer" tab="Customer">
          <div class="layer-bar">
            <div class="layer-bar__field">
              <div class="layer-bar__label">Account</div>
              <div class="layer-bar__value">{{ accountLabel }}</div>
            </div>
            <div class="layer-bar__actions">
              <a-button :loading="loading.customer" @click="reloadCustomer">Reload</a-button>
              <a-button
                :disabled="loading.customer || saving"
                @click="loadDefaultsCustomer"
              >
                Reset to defaults
              </a-button>
              <a-button type="primary" :loading="saving" @click="saveCustomer">Save</a-button>
              <a-button
                type="danger"
                ghost
                :disabled="!hasRow.customer || loading.customer"
                :loading="saving"
                @click="deleteCustomer"
              >
                Delete override
              </a-button>
            </div>
          </div>

          <RenderingLayerForm
            ref="customerForm"
            :stored="stored.customer"
            :inherited="inheritedByTab.customer"
            :shared="sharedByTab.customer"
            :supported-keys="supportedKeysByTab.customer"
            :customer-keys="customerKeys"
            :inherited-engines="inheritedEnginesByTab.customer"
            :is-staff="isStaff"
            :no-stored-row="!hasRow.customer"
            inherit-message="No override is stored here. Values below are inherited from engine defaults."
            :engines="engines"
            :user-agents="userAgents"
            @engine-change="onEngineChange('customer', $event)"
          />
        </a-tab-pane>

        <a-tab-pane :key="tabs.fqdn" tab="FQDN">
          <div class="layer-bar">
            <div class="layer-bar__field">
              <div class="layer-bar__label">Domain (FQDN)</div>
              <a-select
                v-model="selectedFqdnId"
                class="layer-bar__select"
                show-search
                option-filter-prop="children"
                placeholder="Select a domain"
                :loading="loading.fqdns"
                :dropdown-match-select-width="false"
                @change="onFqdnSelected"
              >
                <a-select-option v-for="fqdn in fqdns" :key="fqdn.fqdn_id" :value="fqdn.fqdn_id">
                  {{ fqdn.hostname }}
                </a-select-option>
              </a-select>
            </div>
            <div class="layer-bar__actions">
              <a-button :disabled="!selectedFqdnId" :loading="loading.fqdn" @click="reloadFqdn">
                Reload
              </a-button>
              <a-button
                :disabled="!selectedFqdnId || loading.fqdn || saving"
                @click="loadDefaultsFqdn"
              >
                Reset to defaults
              </a-button>
              <a-button
                type="primary"
                :disabled="!selectedFqdnId || loading.fqdn"
                :loading="saving"
                @click="saveFqdn"
              >
                Save
              </a-button>
              <a-button
                type="danger"
                ghost
                :disabled="!selectedFqdnId || !hasRow.fqdn || loading.fqdn"
                :loading="saving"
                @click="deleteFqdn"
              >
                Delete override
              </a-button>
            </div>
          </div>

          <a-alert
            v-if="fqdnMissingSampleUrl"
            class="mb-3"
            type="info"
            show-icon
            message="No webpage in the URL list matches this domain, so engine defaults cannot be previewed."
          />

          <RenderingLayerForm
            v-if="selectedFqdnId"
            ref="fqdnForm"
            :stored="stored.fqdn"
            :inherited="inheritedByTab.fqdn"
            :shared="sharedByTab.fqdn"
            :supported-keys="supportedKeysByTab.fqdn"
            :customer-keys="customerKeys"
            :inherited-engines="inheritedEnginesByTab.fqdn"
            :is-staff="isStaff"
            :no-stored-row="!hasRow.fqdn"
            inherit-message="No override is stored here. Values below are inherited from the customer override, or from engine defaults if none is set."
            :engines="engines"
            :user-agents="userAgents"
            @engine-change="onEngineChange('fqdn', $event)"
          />
          <a-empty v-else description="Select an FQDN to edit its rendering override." />
        </a-tab-pane>

        <a-tab-pane :key="tabs.url" tab="URL">
          <div class="layer-bar">
            <div class="layer-bar__field">
              <div class="layer-bar__label">Webpage URL</div>
              <a-select
                v-model="selectedUrlId"
                class="layer-bar__select"
                show-search
                :filter-option="false"
                placeholder="Select a webpage"
                :loading="loading.urls"
                :dropdown-match-select-width="false"
                @search="onUrlSearch"
                @change="onUrlSelected"
              >
                <a-select-option v-for="url in urls" :key="url.id" :value="url.id">
                  {{ urlLabel(url) }}
                </a-select-option>
              </a-select>
            </div>
            <div class="layer-bar__actions">
              <a-button :disabled="!selectedUrlId" :loading="loading.url" @click="reloadUrl">
                Reload
              </a-button>
              <a-button
                :disabled="!selectedUrlId || loading.url || saving"
                @click="loadDefaultsUrl"
              >
                Reset to defaults
              </a-button>
              <a-button
                type="primary"
                :disabled="!selectedUrlId || loading.url"
                :loading="saving"
                @click="saveUrl"
              >
                Save
              </a-button>
              <a-button
                type="danger"
                ghost
                :disabled="!selectedUrlId || !hasRow.url || loading.url"
                :loading="saving"
                @click="deleteUrl"
              >
                Delete override
              </a-button>
            </div>
          </div>

          <a-alert
            v-if="resolvePreview && resolvePreview.resolvable === false"
            class="mb-3"
            type="warning"
            show-icon
            message="Resolve reported problems for this URL."
          />

          <RenderingLayerForm
            v-if="selectedUrlId"
            ref="urlForm"
            :stored="stored.url"
            :inherited="inheritedByTab.url"
            :shared="sharedByTab.url"
            :supported-keys="supportedKeysByTab.url"
            :customer-keys="customerKeys"
            :inherited-engines="inheritedEnginesByTab.url"
            :is-staff="isStaff"
            :no-stored-row="!hasRow.url"
            inherit-message="No override is stored here. Values below are inherited from the FQDN override, then the customer override, then engine defaults."
            :engines="engines"
            :user-agents="userAgents"
            @engine-change="onEngineChange('url', $event)"
          />
          <a-empty v-else description="Select a URL to edit its rendering override." />
        </a-tab-pane>
      </a-tabs>
    </div>
  </div>
</template>

<script>
  import WebpageService from '@modules/Webpages/services/webpage.service';
  import RenderingService from '../services/rendering.service';
  import { waitForUserData } from '../../../store/user';
  import RenderingLayerForm from './RenderingLayerForm.vue';
  import renderingAccess from '../mixins/renderingAccess.mixin';
  import {
    RENDERING_TABS,
    FALLBACK_HTML_ENGINE,
    FALLBACK_SCREENSHOT_ENGINE,
    getApiErrorMessage,
    engineRequestDefaults,
    emptyConfigPair,
  } from '../consts/RenderingConfig.consts';

  function emptyLayerForm() {
    return {
      html_engine: null,
      screenshot_engine: null,
      html_config: {},
      screenshot_config: {},
      curl_config: {},
    };
  }

  function firstArray(primary, fallback) {
    if (Array.isArray(primary)) {
      return primary;
    }
    return Array.isArray(fallback) ? fallback : null;
  }

  export default {
    name: 'RenderingConfigPage',
    components: { RenderingLayerForm },
    mixins: [renderingAccess],
    data() {
      return {
        tabs: RENDERING_TABS,
        activeTab: RENDERING_TABS.customer,
        bootLoading: true,
        saving: false,
        engines: [],
        /** API key_policy; customer_keys drives the public/staff field split. */
        keyPolicy: null,
        fqdns: [],
        urls: [],
        urlSearchTimer: null,
        urlSearchRequest: 0,
        selectedFqdnId: null,
        selectedUrlId: null,
        /** The ids the forms hold, so a cancelled switch can put the select back. */
        loadedFqdnId: null,
        loadedUrlId: null,
        fqdnSampleLoading: false,
        fqdnSampleError: false,
        // Each tab may still resolve a URL for key_policy, shared_pipeline, and
        // per-section fallbacks. Inherited engines come from the parent layer chain.
        resolved: {
          customer: null,
          fqdn: null,
          url: null,
        },
        resolveSource: {
          customer: null,
          fqdn: null,
          url: null,
        },
        layerRequest: {
          customer: 0,
          fqdn: 0,
          url: 0,
        },
        resolveRequest: {
          customer: 0,
          fqdn: 0,
          url: 0,
        },
        // Engine codes staff picked but has not saved. Cleared on every reload. The
        // stored layer and the parent chain then drive the selects again.
        engineChoice: {
          customer: null,
          fqdn: null,
          url: null,
        },
        stored: {
          customer: emptyLayerForm(),
          fqdn: emptyLayerForm(),
          url: emptyLayerForm(),
        },
        hasRow: {
          customer: false,
          fqdn: false,
          url: false,
        },
        loading: {
          customer: false,
          fqdn: false,
          url: false,
          fqdns: false,
          urls: false,
        },
      };
    },
    computed: {
      /** The email of the account this customer layer writes to. */
      accountLabel() {
        const user = (this.$store.state.user && this.$store.state.user.data) || {};
        return user.username || user.email || user.name || 'This account';
      },
      resolvePreview() {
        return this.resolved.url;
      },
      /** True when the FQDN tab has a domain but no loaded webpage to resolve defaults from. */
      fqdnMissingSampleUrl() {
        return Boolean(
          this.selectedFqdnId
          && !this.loading.fqdn
          && !this.loading.urls
          && !this.fqdnSampleLoading
          && !this.fqdnSampleError
          && !this.sampleUrlIdForFqdn(this.selectedFqdnId)
        );
      },
      /** customer_keys from API key_policy; null until engines or resolve loads it. */
      customerKeys() {
        return (this.keyPolicy && this.keyPolicy.customer_keys) || null;
      },
      /**
       * Pair a layer inherits when no parent stores a code. key_policy.fallback_engines
       * wins. The local constants cover the boot window before that field arrives.
       */
      fallbackEngines() {
        const reported = (this.keyPolicy && this.keyPolicy.fallback_engines) || {};
        return {
          html_engine: reported.html_engine || FALLBACK_HTML_ENGINE,
          screenshot_engine: reported.screenshot_engine || FALLBACK_SCREENSHOT_ENGINE,
        };
      },
      engineByCodeMap() {
        const map = Object.create(null);
        (this.engines || []).forEach((engine) => {
          if (engine && engine.engine_code) {
            map[engine.engine_code] = engine;
          }
        });
        return map;
      },
      /**
       * Customer stored layer, plus the FQDN layer that owns the selected URL.
       * Only resolve.layers.fqdn is that parent. Do not invent one from the FQDN tab.
       */
      parentLayers() {
        const urlLayers = (this.resolved.url && this.resolved.url.layers) || {};
        return {
          customer: this.stored.customer || {},
          urlFqdn: this.layerFromResponse(urlLayers.fqdn),
        };
      },
      inheritedEnginesByTab() {
        const fallbacks = this.fallbackEngines;
        const customer = this.parentLayers.customer;
        const urlFqdn = this.parentLayers.urlFqdn;
        const urlReady = Boolean(this.resolved.url);
        return {
          [RENDERING_TABS.customer]: {
            html_engine: fallbacks.html_engine,
            screenshot_engine: fallbacks.screenshot_engine,
          },
          [RENDERING_TABS.fqdn]: {
            html_engine: customer.html_engine || fallbacks.html_engine,
            screenshot_engine: customer.screenshot_engine || fallbacks.screenshot_engine,
          },
          [RENDERING_TABS.url]: urlReady
            ? {
                html_engine:
                  urlFqdn.html_engine || customer.html_engine || fallbacks.html_engine,
                screenshot_engine:
                  urlFqdn.screenshot_engine
                  || customer.screenshot_engine
                  || fallbacks.screenshot_engine,
              }
            : {
                html_engine: null,
                screenshot_engine: null,
              },
        };
      },
      effectiveEnginesByTab() {
        const out = {};
        Object.keys(this.inheritedEnginesByTab).forEach((tab) => {
          const inherited = this.inheritedEnginesByTab[tab];
          const own = this.engineChoice[tab] || this.stored[tab] || {};
          out[tab] = {
            html_engine: own.html_engine || inherited.html_engine,
            screenshot_engine: own.screenshot_engine || inherited.screenshot_engine,
          };
        });
        return out;
      },
      sharedByTab() {
        const out = {};
        Object.keys(this.effectiveEnginesByTab).forEach((tab) => {
          out[tab] = this.sharedFor(tab);
        });
        return out;
      },
      supportedKeysByTab() {
        const out = {};
        Object.keys(this.effectiveEnginesByTab).forEach((tab) => {
          out[tab] = this.supportedKeysFor(tab);
        });
        return out;
      },
      engineDefaultsByTab() {
        const out = {};
        Object.keys(this.effectiveEnginesByTab).forEach((tab) => {
          out[tab] = this.engineDefaultsFor(tab);
        });
        return out;
      },
      /**
       * What each tab inherits when it stores nothing: the engine defaults of that tab,
       * then every layer above it.
       */
      inheritedByTab() {
        const customer = this.parentLayers.customer;
        const urlFqdn = this.parentLayers.urlFqdn;
        const merge = (...configs) => Object.assign({}, ...configs);
        const customerDefaults = this.engineDefaultsByTab[RENDERING_TABS.customer];
        const fqdnDefaults = this.engineDefaultsByTab[RENDERING_TABS.fqdn];
        const urlDefaults = this.engineDefaultsByTab[RENDERING_TABS.url];
        return {
          [RENDERING_TABS.customer]: {
            html_config: merge(customerDefaults.html_config),
            screenshot_config: merge(customerDefaults.screenshot_config),
            curl_config: {},
          },
          [RENDERING_TABS.fqdn]: {
            html_config: merge(fqdnDefaults.html_config, customer.html_config || {}),
            screenshot_config: merge(
              fqdnDefaults.screenshot_config,
              customer.screenshot_config || {}
            ),
            curl_config: merge(customer.curl_config || {}),
          },
          [RENDERING_TABS.url]: {
            html_config: merge(
              urlDefaults.html_config,
              customer.html_config || {},
              urlFqdn.html_config || {}
            ),
            screenshot_config: merge(
              urlDefaults.screenshot_config,
              customer.screenshot_config || {},
              urlFqdn.screenshot_config || {}
            ),
            curl_config: merge(customer.curl_config || {}, urlFqdn.curl_config || {}),
          },
        };
      },
    },
    async mounted() {
      await this.bootstrap();
    },
    beforeDestroy() {
      clearTimeout(this.urlSearchTimer);
    },
    watch: {
      '$route.query': {
        deep: true,
        handler() {
          this.applyQuery();
        },
      },
    },
    methods: {
      urlLabel(url) {
        const name = url.config_name || url.name || '';
        const href = url.url_name || url.url || '';
        return name ? `${name} — ${href}` : href || `URL #${url.id}`;
      },
      /**
       * Customers never receive engine codes. Use the API shared_pipeline flag for them.
       * Staff compare the codes this tab's selects show.
       */
      sharedFor(tab) {
        if (!this.isStaff) {
          return Boolean(this.resolved[tab] && this.resolved[tab].shared_pipeline);
        }
        const engines = this.effectiveEnginesByTab[tab] || {};
        return Boolean(
          engines.html_engine && engines.html_engine === engines.screenshot_engine
        );
      },
      engineByCode(code) {
        return (code && this.engineByCodeMap[code]) || null;
      },
      /**
       * Defaults for this tab's selects, one section at a time. A missing catalog row
       * must not wipe the other section's resolve defaults.
       */
      engineDefaultsFor(tab) {
        const engines = this.effectiveEnginesByTab[tab] || {};
        const htmlEngine = this.engineByCode(engines.html_engine);
        const shotEngine = this.engineByCode(engines.screenshot_engine);
        const reported = (this.resolved[tab] && this.resolved[tab].engine_defaults) || {};
        return {
          html_config: htmlEngine
            ? engineRequestDefaults(htmlEngine)
            : reported.html_config || {},
          screenshot_config: shotEngine
            ? engineRequestDefaults(shotEngine)
            : reported.screenshot_config || {},
        };
      },
      /**
       * Layer keys for this tab's selects, one section at a time. Catalog first. The
       * other section may still use resolve when its catalog row is missing.
       */
      supportedKeysFor(tab) {
        const engines = this.effectiveEnginesByTab[tab] || {};
        const reported = (this.resolved[tab] && this.resolved[tab].layer_keys) || {};
        const htmlKeys = firstArray(
          this.engineLayerKeys(engines.html_engine, 'html_config'),
          reported.html_config
        );
        const shotKeys = firstArray(
          this.engineLayerKeys(engines.screenshot_engine, 'screenshot_config'),
          reported.screenshot_config
        );
        if (!htmlKeys && !shotKeys) {
          return null;
        }
        return {
          html_config: htmlKeys,
          screenshot_config: shotKeys,
        };
      },
      engineLayerKeys(engineCode, sectionName) {
        const engine = this.engineByCode(engineCode);
        const keys = (engine && engine.layer_keys) || null;
        return keys && Array.isArray(keys[sectionName]) ? keys[sectionName] : null;
      },
      /** Staff picked an engine on this tab. Inherited values follow the computed chain. */
      onEngineChange(tab, engines) {
        this.$set(this.engineChoice, tab, engines || null);
      },
      /** Strip the scheme, port, and path so a URL can be matched to an FQDN hostname. */
      hostOf(urlName) {
        return String(urlName || '')
          .replace(/^[a-z0-9+.-]+:\/\//i, '')
          .split('/')[0]
          .split(':')[0]
          .toLowerCase();
      },
      sampleUrlIdForFqdn(fqdnId) {
        const fqdn = this.fqdns.find((item) => item.fqdn_id === fqdnId);
        if (!fqdn) {
          return null;
        }
        const host = String(fqdn.hostname || '').toLowerCase();
        const match = this.urls.find((url) => this.hostOf(url.url_name || url.url) === host);
        return match ? match.id : null;
      },
      async ensureFqdnSampleUrl(fqdnId) {
        const loaded = this.sampleUrlIdForFqdn(fqdnId);
        if (loaded) {
          return loaded;
        }
        const fqdn = this.fqdns.find((item) => item.fqdn_id === fqdnId);
        if (!fqdn) {
          return null;
        }
        const host = String(fqdn.hostname || '').toLowerCase();
        this.fqdnSampleError = false;
        this.fqdnSampleLoading = true;
        try {
          const list = await WebpageService.getSimpleURLList({
            page: 1,
            itemsPerPage: 20,
            q: host,
          });
          const match = list.find((url) => this.hostOf(url.url_name || url.url) === host);
          if (match && !this.urls.some((url) => url.id === match.id)) {
            this.urls.push(match);
          }
          return match ? match.id : null;
        } catch (e) {
          this.fqdnSampleError = true;
          this.$showErrorMessage(getApiErrorMessage(e, 'Failed to find a sample URL.'));
          return null;
        } finally {
          this.fqdnSampleLoading = false;
        }
      },
      async ensureSelectedUrlLoaded() {
        if (!this.selectedUrlId || this.urls.some((url) => url.id === this.selectedUrlId)) {
          return;
        }
        try {
          const url = await WebpageService.get(this.selectedUrlId);
          if (url && url.id) {
            this.urls.unshift(url);
          }
        } catch (e) {
          // reloadUrl reports an inaccessible or missing deep-linked URL.
        }
      },
      /**
       * Resolve the engine context a tab inherits. The Customer and FQDN tabs have no URL
       * of their own, so they borrow one that sits under them.
       */
      async refreshEngineContext(tab, urlId) {
        if (this.resolveSource[tab] === urlId && this.resolved[tab]) {
          return true;
        }
        const request = ++this.resolveRequest[tab];
        this.resolveSource[tab] = urlId;
        if (!urlId) {
          this.resolved[tab] = null;
          return true;
        }
        try {
          const resolved = await RenderingService.resolve(urlId);
          if (
            request !== this.resolveRequest[tab]
            || this.resolveSource[tab] !== urlId
          ) {
            return false;
          }
          this.resolved[tab] = resolved;
          if (resolved && resolved.key_policy) {
            this.keyPolicy = resolved.key_policy;
          }
        } catch (e) {
          if (
            request !== this.resolveRequest[tab]
            || this.resolveSource[tab] !== urlId
          ) {
            return false;
          }
          this.resolved[tab] = null;
          this.$showErrorMessage(
            getApiErrorMessage(e, 'Failed to load rendering engine defaults.')
          );
        }
        return true;
      },
      layerFromResponse(data) {
        if (!data || typeof data !== 'object') {
          return emptyLayerForm();
        }
        return {
          html_engine: data.html_engine || null,
          screenshot_engine: data.screenshot_engine || null,
          html_config: data.html_config || {},
          screenshot_config: data.screenshot_config || {},
          curl_config: data.curl_config || {},
        };
      },
      async bootstrap() {
        this.bootLoading = true;
        try {
          const user = (this.$store.state.user && this.$store.state.user.data) || {};
          if (!user.feature_access) {
            await waitForUserData();
          }
          const tasks = [
            this.reloadCustomer(false),
            this.loadFqdns(),
            this.loadUrls(),
            this.loadUserAgents(),
          ];
          if (this.isStaff) {
            tasks.push(this.loadEngines());
          }
          await Promise.all(tasks);
          this.applyQuery(false);
          await this.ensureSelectedUrlLoaded();
          await Promise.all([this.reloadFqdn(), this.reloadUrl()]);
          // The customer reload ran before the URL list arrived, so it had no URL to resolve.
          await this.refreshEngineContext('customer', this.urls.length ? this.urls[0].id : null);
        } catch (e) {
          this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load rendering config.'));
        } finally {
          this.bootLoading = false;
        }
      },
      /**
       * Applies tab and selection from the query string. An explicit `tab` wins over the
       * id parameters, so onTabChange can move away from a deep link that still carries
       * fqdn_id or url_id. A selection only reloads when the id really changed, because
       * this runs again after every query change.
       */
      applyQuery(load = true) {
        const query = this.$route.query || {};
        const tab = query.tab;
        const hasTab =
          tab === RENDERING_TABS.fqdn ||
          tab === RENDERING_TABS.url ||
          tab === RENDERING_TABS.customer;
        if (hasTab) {
          this.activeTab = tab;
        }
        if (query.fqdn_id) {
          const fqdnId = Number(query.fqdn_id);
          if (!Number.isNaN(fqdnId)) {
            if (!hasTab) {
              this.activeTab = RENDERING_TABS.fqdn;
            }
            if (this.selectedFqdnId !== fqdnId) {
              this.selectedFqdnId = fqdnId;
              this.clearLayerForSelection('fqdn');
              if (load) {
                this.reloadFqdn();
              }
            }
          }
        }
        if (query.url_id) {
          const urlId = Number(query.url_id);
          if (!Number.isNaN(urlId)) {
            if (!hasTab) {
              this.activeTab = RENDERING_TABS.url;
            }
            if (this.selectedUrlId !== urlId) {
              this.selectedUrlId = urlId;
              this.clearLayerForSelection('url');
              this.ensureSelectedUrlLoaded();
              if (load) {
                this.reloadUrl();
              }
            }
          }
        }
      },
      onTabChange(key) {
        this.activeTab = key;
        this.updateQuery({ tab: key });
      },
      /**
       * Keep the route in step with the selects. A stale fqdn_id or url_id would make the
       * $route.query watcher put the old resource back on the next navigation, which skips
       * the discard prompt and drops unsaved input.
       */
      updateQuery(patch) {
        const current = this.$route.query || {};
        const next = Object.assign({}, current, patch);
        Object.keys(next).forEach((key) => {
          if (next[key] === null || next[key] === undefined || next[key] === '') {
            delete next[key];
          }
        });
        const keys = Object.keys(next);
        const unchanged =
          keys.length === Object.keys(current).length
          && keys.every((key) => String(current[key]) === String(next[key]));
        if (unchanged) {
          return;
        }
        this.$router.replace({ query: next }).catch(() => {});
      },
      async loadEngines() {
        try {
          const packed = await RenderingService.listEngines(this.engineCacheKey);
          this.engines = Array.isArray(packed)
            ? packed
            : ((packed && packed.engines) || []);
          if (packed && packed.key_policy) {
            this.keyPolicy = packed.key_policy;
          }
        } catch (e) {
          // Without this list the engine selects are empty, so the reason must be visible.
          this.engines = [];
          this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load rendering engines.'));
        }
      },
      async loadFqdns() {
        this.loading.fqdns = true;
        try {
          const list = await RenderingService.listFqdns();
          this.fqdns = Array.isArray(list) ? list : [];
          if (!this.selectedFqdnId && this.fqdns.length) {
            this.selectedFqdnId = this.fqdns[0].fqdn_id;
          }
        } catch (e) {
          this.fqdns = [];
          this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load FQDNs.'));
        } finally {
          this.loading.fqdns = false;
        }
      },
      onUrlSearch(query) {
        clearTimeout(this.urlSearchTimer);
        this.urlSearchTimer = setTimeout(() => {
          this.loadUrls(query);
        }, 300);
      },
      /**
       * Load one server-filtered page for the URL select. Do not call getSimpleURLList
       * without page: that mode downloads every page in parallel.
       */
      async loadUrls(query = '') {
        const request = ++this.urlSearchRequest;
        this.loading.urls = true;
        try {
          const list = await WebpageService.getSimpleURLList({
            page: 1,
            itemsPerPage: 100,
            q: String(query || '').trim(),
          });
          if (request !== this.urlSearchRequest) {
            return;
          }
          const selected = this.urls.find((url) => url.id === this.selectedUrlId);
          this.urls = selected && !list.some((url) => url.id === selected.id)
            ? [selected, ...list]
            : list;
          if (!this.selectedUrlId && this.urls.length) {
            this.selectedUrlId = this.urls[0].id;
          }
        } catch (e) {
          if (request === this.urlSearchRequest) {
            this.urls = [];
            this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load URLs.'));
          }
        } finally {
          if (request === this.urlSearchRequest) {
            this.loading.urls = false;
          }
        }
      },
      /**
       * A reload of one layer must show the server values on that tab, even when they equal
       * what is on screen. The forms of the other tabs keep their unsaved input, because
       * Ant Design keeps every tab pane mounted.
       */
      resetForm(refName) {
        this.$nextTick(() => {
          const form = this.$refs[refName];
          if (form) {
            form.resetFromProps();
          }
        });
      },
      clearLayerForSelection(tab) {
        this.stored[tab] = emptyLayerForm();
        this.hasRow[tab] = false;
        this.resolved[tab] = null;
        this.resolveSource[tab] = null;
        this.engineChoice[tab] = null;
        if (tab === RENDERING_TABS.fqdn) {
          this.fqdnSampleError = false;
        }
      },
      async reloadCustomer(showSpinner = true) {
        const request = ++this.layerRequest.customer;
        if (showSpinner) {
          this.loading.customer = true;
        }
        this.engineChoice.customer = null;
        try {
          const data = await RenderingService.getCustomerConfig();
          if (request !== this.layerRequest.customer) {
            return;
          }
          if (data && data.id) {
            this.hasRow.customer = true;
            this.stored.customer = this.layerFromResponse(data);
          } else {
            this.hasRow.customer = false;
            this.stored.customer = emptyLayerForm();
          }
          await this.refreshEngineContext(
            'customer',
            this.urls.length ? this.urls[0].id : null
          );
        } catch (e) {
          if (request !== this.layerRequest.customer) {
            return;
          }
          this.hasRow.customer = false;
          this.stored.customer = emptyLayerForm();
          if (!(e && e.response && e.response.status === 404)) {
            this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load customer config.'));
          }
        } finally {
          if (request === this.layerRequest.customer) {
            this.resetForm('customerForm');
            if (showSpinner) {
              this.loading.customer = false;
            }
          }
        }
      },
      /**
       * Loading another resource replaces the form, so unsaved input on the current one
       * would be gone. Ask first, and put the select back when the user keeps the edits.
       */
      confirmDiscard(refName, onOk, onCancel) {
        const form = this.$refs[refName];
        if (!form || !form.hasUnsavedChanges()) {
          onOk();
          return;
        }
        this.$confirm({
          title: 'Discard unsaved changes?',
          content: 'The values you changed on this level are not saved yet.',
          onOk,
          onCancel,
        });
      },
      onFqdnSelected() {
        this.confirmDiscard(
          'fqdnForm',
          () => {
            this.updateQuery({
              fqdn_id: this.selectedFqdnId ? String(this.selectedFqdnId) : null,
            });
            this.clearLayerForSelection('fqdn');
            this.reloadFqdn();
          },
          () => {
            this.selectedFqdnId = this.loadedFqdnId;
          }
        );
      },
      async reloadFqdn() {
        const fqdnId = this.selectedFqdnId;
        if (!fqdnId) {
          return;
        }
        this.loadedFqdnId = fqdnId;
        const request = ++this.layerRequest.fqdn;
        this.loading.fqdn = true;
        this.engineChoice.fqdn = null;
        try {
          const data = await RenderingService.getFqdnConfig(fqdnId);
          if (request !== this.layerRequest.fqdn || this.selectedFqdnId !== fqdnId) {
            return;
          }
          if (data && data.id) {
            this.hasRow.fqdn = true;
            this.stored.fqdn = this.layerFromResponse(data);
          } else {
            this.hasRow.fqdn = false;
            this.stored.fqdn = emptyLayerForm();
          }
          const sampleUrlId = await this.ensureFqdnSampleUrl(fqdnId);
          if (request !== this.layerRequest.fqdn || this.selectedFqdnId !== fqdnId) {
            return;
          }
          await this.refreshEngineContext(
            'fqdn',
            sampleUrlId
          );
        } catch (e) {
          if (request !== this.layerRequest.fqdn || this.selectedFqdnId !== fqdnId) {
            return;
          }
          this.hasRow.fqdn = false;
          this.stored.fqdn = emptyLayerForm();
          this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load FQDN config.'));
        } finally {
          if (request === this.layerRequest.fqdn && this.selectedFqdnId === fqdnId) {
            this.resetForm('fqdnForm');
            this.loading.fqdn = false;
          }
        }
      },
      onUrlSelected() {
        this.confirmDiscard(
          'urlForm',
          () => {
            this.updateQuery({
              url_id: this.selectedUrlId ? String(this.selectedUrlId) : null,
            });
            this.clearLayerForSelection('url');
            this.reloadUrl();
          },
          () => {
            this.selectedUrlId = this.loadedUrlId;
          }
        );
      },
      async reloadUrl() {
        const urlId = this.selectedUrlId;
        if (!urlId) {
          return;
        }
        this.loadedUrlId = urlId;
        const request = ++this.layerRequest.url;
        this.loading.url = true;
        this.engineChoice.url = null;
        this.resolveSource.url = null;
        try {
          const data = await RenderingService.getUrlConfig(urlId);
          if (request !== this.layerRequest.url || this.selectedUrlId !== urlId) {
            return;
          }
          if (data && data.id) {
            this.hasRow.url = true;
            this.stored.url = this.layerFromResponse(data);
          } else {
            this.hasRow.url = false;
            this.stored.url = emptyLayerForm();
          }
          await this.refreshEngineContext('url', urlId);
        } catch (e) {
          if (request !== this.layerRequest.url || this.selectedUrlId !== urlId) {
            return;
          }
          this.hasRow.url = false;
          this.stored.url = emptyLayerForm();
          this.$showErrorMessage(getApiErrorMessage(e, 'Failed to load URL config.'));
        } finally {
          if (request === this.layerRequest.url && this.selectedUrlId === urlId) {
            this.resetForm('urlForm');
            this.loading.url = false;
          }
        }
      },
      /** A write can change the selected engine, so the cached resolve must not be reused. */
      invalidateEngineContext() {
        this.resolveSource.customer = null;
        this.resolveSource.fqdn = null;
        this.resolveSource.url = null;
      },
      /** The URL each tab resolves its engine context from. */
      resolveSourceIdFor(tab) {
        if (tab === RENDERING_TABS.fqdn) {
          return this.selectedFqdnId ? this.sampleUrlIdForFqdn(this.selectedFqdnId) : null;
        }
        if (tab === RENDERING_TABS.url) {
          return this.selectedUrlId;
        }
        return this.urls.length ? this.urls[0].id : null;
      },
      /**
       * A layer below the saved one inherits from it, so its engine defaults, supported
       * keys, and pipeline mode change with the write. Resolve those tabs again, or they
       * keep describing the engine context from before the save until the user reloads.
       *
       * sources holds the URL each tab resolved from before the write, because
       * invalidateEngineContext() drops them and the FQDN tab may have found its sample
       * URL through a search that this path must not repeat.
       */
      async refreshDependentContext(savedTab, sources = {}) {
        const tabs = Object.values(RENDERING_TABS).filter((tab) => tab !== savedTab);
        await Promise.all(
          tabs.map((tab) =>
            this.refreshEngineContext(tab, sources[tab] || this.resolveSourceIdFor(tab))
          )
        );
      },
      payloadFromForm(refName) {
        const form = this.$refs[refName];
        if (!form) {
          return null;
        }
        return form.buildPayload();
      },
      async saveLayer({ tab, formRef, requireId, upsert, reload, success, fail }) {
        if (requireId && !requireId()) {
          return;
        }
        const payload = this.payloadFromForm(formRef);
        if (!payload) {
          return;
        }
        const sources = Object.assign({}, this.resolveSource);
        this.saving = true;
        try {
          await upsert(payload);
          this.invalidateEngineContext();
          await reload();
          await this.refreshDependentContext(tab, sources);
          this.$showSuccessMessage(success);
        } catch (e) {
          this.$showErrorMessage(getApiErrorMessage(e, fail));
        } finally {
          this.saving = false;
        }
      },
      deleteLayer({ tab, requireId, stillCurrent, title, content, destroy, reload, success, fail }) {
        if (requireId && !requireId()) {
          return;
        }
        if (!this.hasRow[tab]) {
          return;
        }
        this.$confirm({
          title,
          content,
          onOk: async () => {
            if (stillCurrent && !stillCurrent()) {
              this.$showErrorMessage(
                'The selected resource changed. Delete was cancelled.'
              );
              return;
            }
            const sources = Object.assign({}, this.resolveSource);
            this.saving = true;
            try {
              let alreadyGone = false;
              await Promise.resolve(destroy()).catch((e) => {
                if (!(e && e.response && e.response.status === 404)) {
                  throw e;
                }
                alreadyGone = true;
              });
              this.invalidateEngineContext();
              await reload();
              await this.refreshDependentContext(tab, sources);
              if (alreadyGone) {
                this.$showInfoMessage('Override was already removed.');
              } else {
                this.$showSuccessMessage(success);
              }
            } catch (e) {
              this.$showErrorMessage(getApiErrorMessage(e, fail));
            } finally {
              this.saving = false;
            }
          },
        });
      },
      /**
       * Fill the form with inherited values. Nothing is written until Save.
       */
      resetToInherited(formRef, tab, stillCurrent) {
        const form = this.$refs[formRef];
        if (!form) {
          return;
        }
        this.$confirm({
          title: 'Reset to default values?',
          content:
            'Your changes on this level are dropped from the form. Click Save to apply.',
          onOk: () => {
            if (stillCurrent && !stillCurrent()) {
              this.$showErrorMessage(
                'The selected resource changed. Reset was cancelled.'
              );
              return;
            }
            const liveForm = this.$refs[formRef];
            if (!liveForm) {
              return;
            }
            const inherited = this.inheritedByTab[tab] || emptyConfigPair();
            liveForm.applyDefaults(
              inherited.html_config,
              inherited.screenshot_config,
              inherited.curl_config
            );
            this.$showInfoMessage(
              'Form filled with inherited values. Click Save to apply.'
            );
          },
        });
      },
      loadDefaultsCustomer() {
        this.resetToInherited('customerForm', RENDERING_TABS.customer);
      },
      loadDefaultsFqdn() {
        const fqdnId = this.selectedFqdnId;
        if (!fqdnId) {
          return;
        }
        this.resetToInherited('fqdnForm', RENDERING_TABS.fqdn, () => this.selectedFqdnId === fqdnId);
      },
      loadDefaultsUrl() {
        const urlId = this.selectedUrlId;
        if (!urlId) {
          return;
        }
        this.resetToInherited('urlForm', RENDERING_TABS.url, () => this.selectedUrlId === urlId);
      },
      saveCustomer() {
        return this.saveLayer({
          tab: RENDERING_TABS.customer,
          formRef: 'customerForm',
          upsert: (payload) => RenderingService.upsertCustomerConfig(payload),
          reload: () => this.reloadCustomer(),
          success: 'Customer rendering config saved.',
          fail: 'Failed to save customer config.',
        });
      },
      deleteCustomer() {
        this.deleteLayer({
          tab: RENDERING_TABS.customer,
          title: 'Delete customer override?',
          content: 'Rendering will fall back to engine defaults for account-level values.',
          destroy: () => RenderingService.deleteCustomerConfig(),
          reload: () => this.reloadCustomer(),
          success: 'Customer override deleted.',
          fail: 'Failed to delete customer config.',
        });
      },
      saveFqdn() {
        const fqdnId = this.selectedFqdnId;
        return this.saveLayer({
          tab: RENDERING_TABS.fqdn,
          formRef: 'fqdnForm',
          requireId: () => fqdnId,
          upsert: (payload) => RenderingService.upsertFqdnConfig(fqdnId, payload),
          reload: () => this.selectedFqdnId === fqdnId && this.reloadFqdn(),
          success: 'FQDN rendering config saved.',
          fail: 'Failed to save FQDN config.',
        });
      },
      deleteFqdn() {
        const fqdnId = this.selectedFqdnId;
        this.deleteLayer({
          tab: RENDERING_TABS.fqdn,
          requireId: () => fqdnId,
          stillCurrent: () => this.selectedFqdnId === fqdnId,
          title: 'Delete FQDN override?',
          content: 'Rendering for this domain will fall back to the customer override, or to engine defaults if none is set.',
          destroy: () => RenderingService.deleteFqdnConfig(fqdnId),
          reload: () => this.selectedFqdnId === fqdnId && this.reloadFqdn(),
          success: 'FQDN override deleted.',
          fail: 'Failed to delete FQDN config.',
        });
      },
      saveUrl() {
        const urlId = this.selectedUrlId;
        return this.saveLayer({
          tab: RENDERING_TABS.url,
          formRef: 'urlForm',
          requireId: () => urlId,
          upsert: (payload) => RenderingService.upsertUrlConfig(urlId, payload),
          reload: () => this.selectedUrlId === urlId && this.reloadUrl(),
          success: 'URL rendering config saved.',
          fail: 'Failed to save URL config.',
        });
      },
      deleteUrl() {
        const urlId = this.selectedUrlId;
        this.deleteLayer({
          tab: RENDERING_TABS.url,
          requireId: () => urlId,
          stillCurrent: () => this.selectedUrlId === urlId,
          title: 'Delete URL override?',
          content: 'Rendering for this webpage will fall back to the FQDN override, then the customer override, then engine defaults.',
          destroy: () => RenderingService.deleteUrlConfig(urlId),
          reload: () => this.selectedUrlId === urlId && this.reloadUrl(),
          success: 'URL override deleted.',
          fail: 'Failed to delete URL config.',
        });
      },
    },
  };
</script>

<style scoped>
  /* One selection bar per tab, so all three tabs read the same way. */
  .layer-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 16px;
    padding: 16px 20px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: #fff;
  }
  .layer-bar__field {
    flex: 1 1 300px;
    min-width: 240px;
    max-width: 520px;
  }
  .layer-bar__label {
    font-size: 12px;
    line-height: 18px;
    color: #595959;
    margin-bottom: 4px;
  }
  .layer-bar__select {
    width: 100%;
  }
  .layer-bar__value {
    height: 32px;
    line-height: 32px;
    font-weight: 600;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  /* Pushed right, so the buttons sit in the same place on every tab. */
  .layer-bar__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-left: auto;
  }
</style>
